import { useState, useEffect } from "react";
import { Eyebrow, TopNav, Footer } from "./components/Pieces";
import { Hero } from "./components/Hero";
import { FootstepsBlock } from "./components/Footsteps";
import { WhereSection } from "./components/Where";
import { Tweaks, type TweakValues } from "./components/Tweaks";

const TWEAK_DEFAULTS: TweakValues = { accent: "#ED8B5A" };

export default function App() {
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [values, setValues] = useState<TweakValues>(TWEAK_DEFAULTS);
  const [active, setActive] = useState("top");

  useEffect(() => {
    document.documentElement.style.setProperty("--accent-color", values.accent);
  }, [values.accent]);

  // Keyboard shortcut to toggle tweaks panel: Shift+T (replaces design-tool postMessage hook)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.shiftKey && (e.key === "T" || e.key === "t")) {
        setTweaksOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function handleNav(id: string) {
    setActive(id);
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }

  useEffect(() => {
    function onScroll() {
      const ids = ["who", "where"];
      let current = "top";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top < 160) current = id;
      }
      setActive(current);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="page">
      <TopNav onNav={handleNav} active={active} />
      <Hero onNav={handleNav} />

      <main className="page-main">
        <section className="section" id="who">
          <div className="section-head">
            <Eyebrow accent="point2">AIPQ의 철학 · Philosophy</Eyebrow>
            <h2 className="section-title">
              AI를 다루는 역량이, <span className="muted">무엇보다 중요하다.</span>
            </h2>
            <p className="section-lede">
              프롬프트부터 하네스까지, AIPQ는 최전선에서 학습하며 개발합니다.
            </p>
            <div className="philo-loop" aria-label="AIPQ의 철학 루프">
              <div className="philo-loop-step">
                <div className="philo-loop-num">01</div>
                <div className="philo-loop-text">
                  좋은 하네스를 만들어<br />
                  <strong>AI의 힘을 키우고</strong>
                </div>
              </div>
              <div className="philo-loop-step">
                <div className="philo-loop-num">02</div>
                <div className="philo-loop-text">
                  실제 서비스를 운영하며<br />
                  <strong>하네스를 다시 짠다.</strong>
                </div>
              </div>
            </div>
            <div className="philo-coda">
              <p>
                <strong>모델이 전부가 아닙니다.</strong>
              </p>
              <p>모델의 힘은 그것이 놓이는 환경(하네스)에서 증폭되거나 소실됩니다.</p>
              <p>
                AIPQ는 좋은 환경을 먼저 설계하고, 실제 프로젝트를 운영하며 그 환경을 다시 다듬습니다.
              </p>
            </div>
          </div>
          <FootstepsBlock />
        </section>

        <WhereSection />
      </main>

      <Footer />
      <Tweaks open={tweaksOpen} values={values} onChange={setValues} />
    </div>
  );
}
